import { breadcrumb, reportError } from '../observability/errorReporter';
import { wsConnectGenerationProbe } from '../realtime/wsConnectGenerationProbe';

/**
 * Mobile WebRTC / VoIP call-drop diagnostics (iOS + Android; checklist Steps 1–3, 7–9).
 *
 * **Verbose logs** (`[CALL END]`, `[SIGNAL]`, `[PC]`, `[PC CREATE]`): controlled by
 * {@link CALL_IOS_WEBRTC_VERBOSE_LOGS} (defaults to `__DEV__`). Set to `true` for
 * release builds when capturing device logs.
 *
 * **Bisect (Steps 4–5)** — defaults are **false** (safe for CI / production):
 * - {@link CALL_WEBRTC_BISECT_ALL}: one switch — enables **both** WS generation bypass
 *   and auto-teardown skip (same as flipping the two flags below).
 * - {@link CALL_IOS_WEBRTC_BISECT_DISABLE_WS_CONNECT_GEN_GUARDS}: in
 *   `shouldAbortWsConnect`, ignore generation mismatch (still aborts if the effect
 *   is inactive). If calls become stable, suspect superseded connect / zombie close.
 * - {@link CALL_IOS_WEBRTC_BISECT_DISABLE_AUTO_CALL_TEARDOWN}: skip unmount teardown,
 *   ring timeout auto-bye, and skip disposing a just-created PC when `mediaGenRef`
 *   races. Manual hangup still works. If stable, suspect React lifecycle / duplicate PC.
 */
export const CALL_IOS_WEBRTC_VERBOSE_LOGS =
  typeof __DEV__ !== 'undefined' ? __DEV__ : false;

/**
 * Master bisect: enables {@link CALL_IOS_WEBRTC_BISECT_DISABLE_WS_CONNECT_GEN_GUARDS}
 * and {@link CALL_IOS_WEBRTC_BISECT_DISABLE_AUTO_CALL_TEARDOWN} together.
 * **Must stay false** in committed / CI code.
 */
export const CALL_WEBRTC_BISECT_ALL = false;

/**
 * Fine-grained bisect: ignore WebSocket connect generation mismatch in
 * `shouldAbortWsConnect` (still aborts when the realtime effect is inactive).
 */
export const CALL_IOS_WEBRTC_BISECT_DISABLE_WS_CONNECT_GEN_GUARDS = false;

/**
 * Fine-grained bisect: skip automatic call teardown paths (see module doc above).
 * Manual hangup still runs.
 */
export const CALL_IOS_WEBRTC_BISECT_DISABLE_AUTO_CALL_TEARDOWN = false;

export function effectiveBisectDisableWsConnectGenGuards(): boolean {
  return CALL_WEBRTC_BISECT_ALL || CALL_IOS_WEBRTC_BISECT_DISABLE_WS_CONNECT_GEN_GUARDS;
}

export function effectiveBisectDisableAutoCallTeardown(): boolean {
  return CALL_WEBRTC_BISECT_ALL || CALL_IOS_WEBRTC_BISECT_DISABLE_AUTO_CALL_TEARDOWN;
}

/**
 * Причины завершения, которые сами по себе — поломка, а не штатный конец разговора (OBS-2).
 *
 * <p>Список закрытый, и это осознанно: {@link logCallLifecycleEnd} зовётся из трёх десятков мест,
 * и большинство из них — обычная уборка за собой (положили трубку, размонтировали экран, закрыли
 * сокет при выходе). Отправлять их все значило бы утопить приёмник в шуме и заодно упереться в
 * лимит. Всё остальное всё равно остаётся шагом и приезжает вместе с первой настоящей поломкой.
 */
const ABNORMAL_END_REASONS = new Set([
  'peer_connection_failed_or_closed',
  'call_wait_timeout_outgoing',
  'call_wait_timeout_incoming',
  'signal_call_line_busy_teardown',
  'signal_invite_glare_teardown',
  'prepareCalleePeer_stale_media_generation',
  'CallKeep_endCall_no_pending_payload',
  'CallKeep_endCall_group_calls_disabled',
  'CallKeep_endCall_navigation_not_ready',
]);

/**
 * Чем закончился звонок.
 *
 * <p>Раньше это была строка в консоли и только в отладочной сборке — то есть в релизе причина
 * обрыва не сохранялась нигде, и жалоба «звонок сбросился сам» разбору не поддавалась в принципе.
 * Теперь причина остаётся шагом всегда, а ненормальные исходы ещё и уезжают отчётом (OBS-2).
 */
export function logCallLifecycleEnd(
  reason: string,
  fields: { callId?: string | null; chatId?: string; extra?: Record<string, unknown> } = {},
): void {
  const { callId, chatId, extra } = fields;
  breadcrumb('call', `конец: ${reason}`);
  if (ABNORMAL_END_REASONS.has(reason)) {
    void reportError('call', new Error(`call ended: ${reason}`), {
      level: 'warning',
      context: {
        причина: reason,
        callId: callId ?? undefined,
        chatId,
        поколениеСокета: wsConnectGenerationProbe.current,
      },
    });
  }
  if (!CALL_IOS_WEBRTC_VERBOSE_LOGS) return;
  console.log('[CALL END]', {
    reason,
    ts: Date.now(),
    callId: callId ?? null,
    chatId: chatId ?? null,
    socketGeneration: wsConnectGenerationProbe.current,
    ...extra,
  });
}

export function logCallSignalLine(fields: {
  type: string;
  direction: 'incoming' | 'outgoing';
  callId?: string | null;
  chatId?: string;
}): void {
  // Кандидаты ICE идут десятками за соединение и вытеснили бы из ленты шагов всё остальное.
  // Смысл несут переговорные кадры: по паре invite→answer сразу видно, дошло ли приглашение.
  if (fields.type !== 'candidate') {
    breadcrumb('call', `сигнал ${fields.direction} ${fields.type}`);
  }
  if (!CALL_IOS_WEBRTC_VERBOSE_LOGS) return;
  console.log('[SIGNAL]', {
    ...fields,
    ts: Date.now(),
    socketGeneration: wsConnectGenerationProbe.current,
  });
}

export function logPeerConnectionCreate(fields: { callId?: string | null; chatId?: string }): void {
  if (!CALL_IOS_WEBRTC_VERBOSE_LOGS) return;
  console.log('[PC CREATE]', {
    ...fields,
    ts: Date.now(),
    socketGeneration: wsConnectGenerationProbe.current,
  });
}

export function logPeerConnectionStateLine(
  label: string,
  pc: {
    connectionState?: string;
    iceConnectionState?: string;
    signalingState?: string;
    iceGatheringState?: string;
  },
): void {
  if (!CALL_IOS_WEBRTC_VERBOSE_LOGS) return;
  try {
    console.log(`[PC] ${label}`, {
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      signalingState: pc.signalingState,
      iceGatheringState: pc.iceGatheringState,
      ts: Date.now(),
    });
  } catch {
    console.log(`[PC] ${label}`, { ts: Date.now() });
  }
}
